import { useEffect, useMemo } from 'react';
import Table, { ColumnsType } from 'antd/lib/table';
import { useLocation } from 'react-router-dom';
import { PageHeader } from 'src/components/PageHeader';
import { SearchInput } from 'src/components/searchInput/searchInput';
import { CAISO_SIBR_VIEW, generateDayPeriod } from 'src/pages/caiso/interface';
import { usePageLocation } from 'src/pages/usePageState';
import { WithEmptyDataTable } from 'src/components/table/withEmptyDataTable';
import { ResourceBidDayDto, ResourceBidsDto } from 'src/domain/caiso-resource/interface';
import App from 'antd/lib/app';
import { format, isWithinInterval, subDays } from 'date-fns';
import { getColumnFilteredValue } from 'src/components/table/columnFormatHelpers';
import { sortingComparator } from 'src/domain/common/sortingComparator';
import { toUsefulParams } from 'src/pages/toUsefulParams';
import { useScResourceBidsQuery } from 'src/domain/caiso-resource/queries';
import { queryClient, QueryKey } from 'src/query-client';
import { CaisoBreadcrumbNav } from 'src/components/CaisoBreadcrumb/CaisoBreadcrumbNav';
import { useDocumentTitle } from 'src/components/useDocumentTitle';
import './SibrOverviewView.css';

const statusColors: { [key: string]: string } = {
    rejected: 'red',
    invalid: 'red',
    valid: 'blue',
    modified: 'green',
    'conditionally modified': 'blue',
    'conditionally valid': 'blue',
};

interface SibrOverviewViewProps {
    view: CAISO_SIBR_VIEW;
    handleView: (view: CAISO_SIBR_VIEW) => void;
}

export const SibrOverviewView = ({ view, handleView }: SibrOverviewViewProps) => {
    useDocumentTitle('CAISO SIBR Overview');

    const { notification } = App.useApp();
    const location = useLocation();
    const { setPageQuery, queryToState } = usePageLocation();
    const pageState: any = queryToState(location.search);
    const daysPeriod = generateDayPeriod();
    const resourceNames: string[] = pageState.filter?.resource_name || [];
    const { data, isLoading: loadingResources, error, refetch } = useScResourceBidsQuery(pageState);

    const resourceBids = data || ([] as ResourceBidsDto[]);

    useEffect(() => {
        if (!loadingResources && error) {
            notification.error({
                key: 'caiso-resource-bids-error',
                message: (error as Error)?.message || 'Cannot load bids!',
            });
        }
    }, [loadingResources, error]);

    async function handleSearch(value: string) {
        setPageQuery({ ...pageState, search: value });
        await queryClient.invalidateQueries([QueryKey.SC_RESOURCE_BIDS]);
        refetch();
    }

    const renderStatusDot = (status: string) => (
        <div
            style={{
                backgroundColor: statusColors[status.toLowerCase()],
                width: '0.5em',
                height: '0.5em',
                borderRadius: '50%',
                marginRight: '0.5em',
            }}
        />
    );

    function onTableChange(pagination: any, filter: any, sorter: any) {
        const preparedParams = toUsefulParams({ pagination, filter, sorter });

        setPageQuery({ ...pageState, ...preparedParams });
    }

    const isResourceBidDayDto = (value: any): value is ResourceBidDayDto => {
        return value && typeof value === 'object' && 'status' in value;
    };
    const updateBidStatusBasedOnDate = (bids: ResourceBidsDto[]): ResourceBidsDto[] => {
        const currentDate = new Date();
        return bids.map(bid => {
            const updatedBid = { ...bid };
            daysPeriod.forEach((day, i) => {
                const bidDate = new Date(day);
                if (isWithinInterval(bidDate, { start: subDays(currentDate, 1), end: currentDate })) {
                    const dayKey = `da_days_${i + 1}` as keyof ResourceBidsDto;
                    const dayValue = updatedBid[dayKey];
                    if (isResourceBidDayDto(dayValue) && dayValue.status === 'conditionally modified') {
                        dayValue.status = 'modified';
                    }
                }
            });
            return updatedBid;
        });
    };
    const updatedResourceBids = useMemo(() => updateBidStatusBasedOnDate(resourceBids), [resourceBids]);

    const columns: ColumnsType<ResourceBidsDto> = [
        {
            title: 'Resource',
            dataIndex: 'resource_name',
            minWidth: 250,
            width: 250,
            fixed: 'left',
            sorter: (a, b) => sortingComparator(a.resource_name, b.resource_name),
            filters: resourceBids.map(c => ({ text: c.resource_name, value: c.resource_name })),
            filteredValue: getColumnFilteredValue('resource_name', pageState?.filter || {}).filteredValue,
            filterSearch: (input, record) => {
                return !!(record?.text as string)?.toLowerCase().includes(input.toLowerCase());
            },
        },
        ...daysPeriod.map((day, i) => ({
            title: `${format(new Date(day), 'EEE d')}`,
            dataIndex: ['days', `${format(new Date(day), 'MM/dd')}`],
            key: `${day}`,
            render: (_: any, el: any) => {
                const status = el[`da_days_${i + 1}`]?.status;
                return status ? (
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                        {renderStatusDot(status)}
                        {status?.charAt(0).toUpperCase() + status?.slice(1)}
                    </span>
                ) : null;
            },
            sorter: (a: any, b: any) => sortingComparator(a[`da_days_${i + 1}`]?.status, b[`da_days_${i + 1}`]?.status),
        })),
    ];

    const filteredCaisoResources = useMemo(() => {
        return !resourceNames.length ? updatedResourceBids : updatedResourceBids.filter(resource => resourceNames?.includes(resource.resource_name));
    }, [updatedResourceBids, resourceNames]);

    return (
        <div className="sibr-overview-view">
            <PageHeader
                pageTitle={<CaisoBreadcrumbNav />}
                actions={[<SearchInput key="search-input-sibr-overview-view" onSearch={handleSearch} defaultValue={pageState.search} />]}
            />
            <WithEmptyDataTable condition={loadingResources}>
                <Table
                    size="small"
                    rowKey="id"
                    onChange={onTableChange}
                    columns={columns}
                    dataSource={filteredCaisoResources}
                    pagination={false}
                    loading={loadingResources}
                    sticky={{ offsetHeader: 62 }}
                    tableLayout={'auto'}
                    scroll={{ x: 'scroll' }}
                />
            </WithEmptyDataTable>
        </div>
    );
};
